import React from "react";
import { Autocomplete, InputAdornment, Stack, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { Room } from "../../../interfaces/RoomInterface";
import { Ward } from "../../../interfaces/WardInterface";
import { Filter } from "../Wards/WardBeds";

interface PropsInterface {
  wards?: Array<Ward>;
  rooms: Array<Room>;
  filter: Filter;
  onBedNumberChange: (value: string) => void;
  onWardChange?: (value: Ward | null) => void;
  onRoomChange: (value: Room | null) => void;
}

// eslint-disable-next-line import/prefer-default-export
export const BedSearch = ({
  wards = [],
  rooms = [],
  filter,
  onBedNumberChange,
  onWardChange,
  onRoomChange
}: PropsInterface): React.ReactNode => {
  const roomOptions = React.useMemo(() => {
    if (!filter.selectedWard) return rooms;
    return rooms.filter((room) => Number(room.wardId) === Number(filter.selectedWard?.id));
  }, [rooms, filter.selectedWard]);

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", md: "center" }}
    >
      <TextField
        variant="outlined"
        placeholder="Search bed number..."
        value={filter.bedNumber}
        onChange={(e) => onBedNumberChange(e.target.value)}
        sx={{
          minWidth: 280,
          backgroundColor: "#ECECEC",
          borderRadius: 1,
          "& fieldset": { border: "none" }
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          },
          inputLabel: { shrink: true }
        }}
      />

      <Autocomplete
        size="small"
        sx={{ minWidth: 160 }}
        value={filter.selectedWard ?? null}
        options={wards}
        getOptionLabel={(ward) => `${ward.name}${ward.shortCode ? ` (${ward.shortCode})` : ""}`}
        onChange={(_, v) => {
          onWardChange?.(v);
          if (filter.selectedRoom && Number(filter.selectedRoom.wardId) !== Number(v?.id)) {
            onRoomChange(null);
          }
        }}
        renderInput={(params) => <TextField {...params} variant="outlined" label="Ward" />}
      />

      <Autocomplete
        size="small"
        sx={{ minWidth: 160 }}
        value={filter.selectedRoom ?? null}
        options={roomOptions}
        getOptionLabel={(room) => `${room.name}${room.code ? ` (${room.code})` : ""}`}
        onChange={(_, v) => onRoomChange(v)}
        disabled={!filter.selectedWard}
        renderInput={(params) => <TextField {...params} variant="outlined" label="Room" />}
      />
    </Stack>
  );
};
